// src/utils/api.js
import axios from "axios";
import { handleLogout } from './auth';

export const API_URL =
  process.env.REACT_APP_API_URL || "http://192.168.1.165:8000";

const http = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

http.interceptors.request.use((config) => {
  const token = localStorage.getItem("jwt_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Add response interceptor to handle token errors
http.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const detail = error.response?.data?.detail || error.response?.data?.message;

    // ── Auth token handling ────────────────────────────
    if (
      status === 401 &&
      (detail === "Invalid or expired token" ||
        detail === "Invalid or expired token")
    ) {
      handleLogout("Your session has expired. Please log in again.");
    }

    // ── Usage / rate-limit handling ────────────────────
    // Backend returns 402 (payment required), 403 (forbidden), or 429 (too many requests)
    // whenever the user exceeds one of their subscription limits.
    if (status === 402 || status === 403 || status === 429) {
      // Check if this is a collaboration error that should be handled more gracefully
      const isCollaborationError = typeof detail === 'object' && 
        (detail?.reason === 'subscription_required' || detail?.feature === 'collaborators_per_card');
      
      // Skip showing alert for collaboration errors - let the UI handle them gracefully
      if (isCollaborationError) {
        return Promise.reject(error);
      }
      
      // Show the backend-provided message if available, otherwise fall back.
      // Handle both string and object responses from the backend
      let msg;
      let requiresUpgrade = false;
      
      if (typeof detail === 'string') {
        msg = detail;
        // Check if this is an upgrade-related message
        requiresUpgrade = detail.includes('upgrade') || detail.includes('limit') || detail.includes('plan');
      } else if (typeof detail === 'object' && detail?.message) {
        msg = detail.message;
        requiresUpgrade = detail.upgrade_required || detail.reason === 'subscription_required';
      } else {
        msg = "You have reached your usage limit for this feature. Please upgrade your plan or wait for the limit to reset.";
        requiresUpgrade = true;
      }
      
      // Use the global error handler to show a proper modal
      const errorEvent = new CustomEvent('showError', { 
        detail: { message: msg, requiresUpgrade } 
      });
      window.dispatchEvent(errorEvent);
    }

    return Promise.reject(error);
  }
);

// still in api.js, right below the 401-handling interceptor
http.interceptors.response.use(
  (response) => {
    console.log(
      `%c[Axios] ← ${response.config.method?.toUpperCase()} ${response.config.url}`,
      "color:#0a0",
      response.status,
      response.data
    );
    return response;
  },
  (error) => {
    if (error.response) {
      console.error(
        `[Axios] ← ${error.config.method?.toUpperCase()} ${error.config.url}`,
        error.response.status,
        error.response.data
      );
    } else {
      console.error("[Axios] Network error", error.message);
    }
    return Promise.reject(error);
  }
);

export function setAuthToken(token) {
  if (token) localStorage.setItem("jwt_token", token);
  else localStorage.removeItem("jwt_token");
}

// ── Auth ─────────────────────────────────────────────────────────────
export const login = (creds) =>
  http.post("/login/", creds);
export const register = (creds) =>
  http.post("/register/", creds);
export const resetPassword = (email) =>
  http.post("/reset-password/", { email });
export const googleSignIn = (idToken) =>
  http.post("/google-signin/", { id_token: idToken });

// ── User Profile ─────────────────────────────────────────────────────
export const getUserProfile = () =>
  http.get("/user/profile/");
export const updateUserProfile = (data) =>
  http.put("/user/profile/", data);

// ── Stripe linking & checkout & subscription ──────────────────────────
export const getSubscriptions = () => http.get("/subscriptions/");
export const linkStripeCustomer = (payload) =>
  http.post("/stripe/link_customer/", payload);
export const createStripeCheckout = (line_items) =>
  http.post("/stripe/create-checkout/", { line_items });
export const createStripeSubscription = (price_id, return_url) =>
  http.post("/stripe/create-subscription/", { price_id, return_url });
export const cancelStripeSubscription = (stripe_subscription_id) =>
  http.post("/stripe/cancel-subscription/", { stripe_subscription_id });
// export const getSubscriptions = () => http.get("/stripe/subscriptions/");

// ── Card collaboration & scheduling ─────────────────────────────────
export const inviteContributor = (card_id, contributor_email, sender_name, recipient_name, occasion = "greeting") =>
  http.post(`/api/cards/${card_id}/invite/`, { 
    contributor_email, 
    sender_name, 
    recipient_name, 
    occasion 
  });

export const scheduleCardSend = (
  card_id,
  send_date,
  recipient_email,
  recipient_name = "",
  sender_name
) => {
  // Convert whatever we receive to a proper ISO-8601 string with timezone
  // The <input type="datetime-local"> returns a string WITHOUT timezone. If we
  // send that straight to the backend it is interpreted as UTC, which causes
  // "date in the past" errors for users ahead of UTC. Convert to UTC first.
  let iso;
  if (send_date instanceof Date) {
    iso = send_date.toISOString();
  } else if (typeof send_date === "string") {
    const d = new Date(send_date);
    if (!isNaN(d)) iso = d.toISOString();
  }

  return http.post(`/api/cards/${card_id}/schedule/`, {
    send_date: iso || send_date,
    recipient_email,
    recipient_name,
    sender_name,
  });
};

// ── Existing generation & upload ────────────────────────────────────
export const generateMessage = (formData) =>
  http.post("/generate_card_message/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const dalleGenerate = (formData) =>
  http.post("/dalle_generate/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const getCardDetails = (card_id) =>
  http.get(`/cards/${card_id}/`);
export const getPlans = () => http.get("/subscriptions/plans");
export const dalleEdit = (formData, options = {}) =>
  http.post("/dalle_edit/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 300000, // 5 minute timeout for AI operations
    ...options,
  });

export const geminiEdit = (formData, options = {}) =>
  http.post("/gemini_edit/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 300000, // 5 minute timeout for AI operations
    ...options,
  });

/**
 * Subscribe to progress updates for a DALL-E operation via Server-Sent Events.
 * 
 * @param {string} requestId - The unique request ID to track
 * @param {function} onProgress - Callback function that receives { stage: string, percent: number }
 * @returns {function} - Cleanup function to close the SSE connection
 */
export const subscribeToDalleProgress = (requestId, onProgress) => {
  const eventSource = new EventSource(`${API_URL}/dalle_edit/progress/${requestId}`);
  
  eventSource.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      if (onProgress && data.stage) {
        onProgress(data);
      }
      // Close connection when complete
      if (data.percent >= 100 || data.percent === -1) {
        eventSource.close();
      }
    } catch (e) {
      console.warn("[SSE] Failed to parse progress event:", e);
    }
  };
  
  eventSource.onerror = (error) => {
    console.warn("[SSE] Connection error:", error);
    eventSource.close();
  };
  
  // Return cleanup function
  return () => {
    eventSource.close();
  };
};

/**
 * Generate a unique request ID for tracking progress
 */
export const generateRequestId = () => {
  return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};
export const dalleVariation = (formData) =>
  http.post("/dalle_variation/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const getSubscriptionStatus = () =>
  http.get("/subscriptions/status/");

export const saveCardDraft = async (payload) => {
  try {
    console.log("%c[saveCardDraft] → sending", "color:#0af", payload);
    const res = await http.post("/cards/save_draft/", payload);
    console.log(
      "%c[saveCardDraft] ← success",
      "color:#0a0",
      res.status,
      res.data
    );
    return res.data.id; // Firestore document ID
  } catch (err) {
    if (err.response) {
      console.error(
        "[saveCardDraft] ← backend error",
        err.response.status,
        err.response.data
      );
    } else {
      console.error("[saveCardDraft] ← network error", err.message);
    }
    throw err;
  }
};

export const getDrafts = () =>
  http.get("/api/cards/drafts/");

export const createCard = (payload) =>
  http.post("/cards/", payload).then((res) => res.data);

export const updateCard = (card_id, payload) =>
  http.patch(`/cards/${card_id}/`, payload).then((res) => res.data);

export const getCard = (card_id) =>
  http.get(`/cards/${card_id}/`);

export async function saveCard(card_id, payload) {
  if (!card_id) {
    const data = await createCard(payload);
    return data.card_id;
  } else {
    await updateCard(card_id, payload);
    return card_id;
  }
}

// ------------ templates -------------------------------
export const listTemplates = () =>
  http.get("/templates/");

// Get paginated templates for a specific category
// media_filter: "all" | "image" | "video"
export const listCategoryTemplates = (category, page = 1, limit = 10, mediaFilter = "all") =>
  http.get(`/templates/${category}/list`, { params: { page, limit, media_filter: mediaFilter } });

export const createCardImage = (formData) =>
    http.post("/cards/image/", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

export const uploadTemplate = (formData) =>
  http.post("/templates/upload/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

// Get scheduled cards for the current user
export const getScheduledCards = () =>
  http.get("/api/cards/scheduled/");

// Get card history (scheduled, sent, and opened)
export const getCardHistory = () =>
  http.get("/api/cards/history/");

// Delete a card draft
export const deleteCard = (card_id) =>
  http.delete(`/api/cards/${card_id}/`);

export async function createFreeSubscription() {
  const { data } = await http.post(`/subscriptions/create_free`);
  return data;
}

export async function createPreviewSubscription() {
  const { data } = await http.post(`/subscriptions/create_preview`);
  return data;
}

// ── Card Credit Pack Endpoints ─────────────────────────────────────────────
/**
 * Get all available credit pack options.
 * Credit packs are one-time purchases for users who don't want a subscription.
 */
export const getCreditPacks = () =>
  http.get("/credit-packs/");

/**
 * Purchase a credit pack.
 * Creates a Stripe checkout session for one-time payment.
 * 
 * @param {string} packType - "single" (1 card for $3.99) or "bundle_10" (10 cards for $34.99)
 * @param {string} returnUrl - URL to redirect after successful payment
 * @param {string} cancelUrl - URL to redirect if payment is canceled
 * @returns {Promise<{checkout_url: string}>}
 */
export const purchaseCredits = async (packType, returnUrl, cancelUrl) => {
  const { data } = await http.post("/stripe/purchase-credits/", {
    pack_type: packType,
    return_url: returnUrl,
    cancel_url: cancelUrl,
  });
  return data;
};

/**
 * Get the current credit balance for the authenticated user.
 */
export const getCreditBalance = () =>
  http.get("/credits/balance/");

/**
 * Get the credit transaction history for the authenticated user.
 */
export const getCreditHistory = () =>
  http.get("/credits/history/");

// Public card fetch (no auth required)
export const getPublicCard = (card_id) =>
  http.get(`/cards/public/${card_id}`);

// Mark card as opened (no auth)
export const markCardOpened = (card_id, preview = false) => {
  // Skip marking as opened if this is a preview
  if (preview) {
    console.log("[Card View] Preview mode - skipping opened status update");
    return Promise.resolve();
  }

  // Get auth token if available
  const token = localStorage.getItem("jwt_token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  
  return http.post(
    `/cards/public/${card_id}/opened`,
    {},  // empty body
    { headers }  // include auth headers if available
  );
};

// ── Public collaboration endpoints (no auth required) ─────────────────
export const getCardForCollaboration = (card_id, params = {}) => {
  const queryParams = new URLSearchParams(params).toString();
  const url = queryParams 
    ? `/cards/public/${card_id}/collaborate?${queryParams}`
    : `/cards/public/${card_id}/collaborate`;
  return http.get(url);
};

export const updateCardCollaboration = (card_id, updateData, params = {}) => {
  const queryParams = new URLSearchParams(params).toString();
  const url = queryParams 
    ? `/cards/public/${card_id}/collaborate?${queryParams}`
    : `/cards/public/${card_id}/collaborate`;
  return http.put(url, updateData);
};

export const getCollaborationFeatures = (card_id) =>
  http.get(`/cards/public/${card_id}/collaboration-features`);

// ── Feedback ─────────────────────────────────────────────────────────────
export const submitFeedback = (feedback, reason = null) =>
  http.post("/subscriptions/feedback", { feedback, reason });

// ── AI Video Generation for Greeting Cards ─────────────────────────────────
/**
 * Generate an AI video greeting card.
 * Can create video from:
 * 1. Text prompt only (text-to-video)
 * 2. Image + prompt (image-to-video animation)
 * 
 * @param {Object} options - Video generation options
 * @param {string} options.prompt - Description of the video to generate
 * @param {string} [options.imageUrl] - Optional URL of image to animate
 * @param {File} [options.imageFile] - Optional uploaded image file to animate
 * @param {number} [options.duration=5] - Video duration in seconds (5 or 10)
 * @param {string} [options.model="luma"] - "luma" or "runway"
 * @param {string} [options.aspectRatio="16:9"] - "16:9" or "9:16"
 * @param {string} [options.resolution="720p"] - "540p", "720p", "1080p", or "4k"
 * @returns {Promise<{video_url: string, message: string, media_type: string}>}
 */
export const generateVideoCard = async ({
  prompt,
  imageUrl,
  imageFile,
  duration = 5,
  model = "luma",
  aspectRatio = "16:9",
  resolution = "720p",
}) => {
  const formData = new FormData();
  formData.append("prompt", prompt);
  formData.append("duration", duration.toString());
  formData.append("model", model);
  formData.append("aspect_ratio", aspectRatio);
  formData.append("resolution", resolution);
  
  if (imageUrl) {
    formData.append("image_url", imageUrl);
  }
  if (imageFile) {
    formData.append("image_file", imageFile);
  }
  
  const { data } = await http.post("/generate_video_card/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 300000, // 5 minute timeout for video generation
  });
  
  return data;
};

/**
 * Get template info including media type (image or video)
 */
export const getTemplateInfo = (category, filename) =>
  http.get(`/templates/${category}/${filename}/info`);

// ── Media type helper ─────────────────────────────────────────────────────
/**
 * Determine if a URL points to a video file
 */
export const isVideoUrl = (url) => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv'];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some(ext => lowerUrl.includes(ext));
};

/**
 * Get media type from URL
 */
export const getMediaTypeFromUrl = (url) => {
  return isVideoUrl(url) ? "video" : "image";
};

// ── Photo Swap (Gemini-powered face/subject swap) ─────────────────────────
/**
 * Perform AI-powered photo swap using Gemini.
 * Swaps faces or subjects between a template image and a reference image.
 * 
 * @param {Object} options - Photo swap options
 * @param {string} options.prompt - Description of what to swap
 * @param {File} options.templateImage - The base/template image to edit
 * @param {File} options.referenceImage - The reference image with the face/subject
 * @param {boolean} [options.useProModel=false] - Use higher quality model
 * @returns {Promise<{public_url: string}>}
 */
export const photoSwap = async ({
  prompt,
  templateImage,
  referenceImage,
  useProModel = false,
}) => {
  const formData = new FormData();
  formData.append("prompt", prompt);
  formData.append("template_image", templateImage);
  formData.append("reference_image", referenceImage);
  formData.append("use_pro_model", useProModel ? "true" : "false");
  
  const { data } = await http.post("/photo_swap/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 300000, // 5 minute timeout for AI operations
  });
  
  return data;
};
